/*
* Gift with purchase
*
* Puts the gift offered in the cart (snippets/gwp-offer.liquid) in by itself once the cart
* qualifies, as a line carrying the hidden property _gwp, unless the shopper removed it from this
* cart; the picker stays as the way to add it back, and the gift line's menu swaps its colour or
* size. Gift lines are checked after every cart change: one of each gift, no more gifts than
* Theme settings > Gift with purchase allows, and never charged. The Buy X Get Y discount in admin
* is what makes a gift free, so a gift line it does not cover is taken out.
*
*/

(function() {

  const configEl = document.getElementById('gwp-config');
  if (!configEl || window.gwpLoaded) {
    return;
  }
  window.gwpLoaded = true;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  const property = config.property || '_gwp';
  const giftIds = (config.giftProductIds || []).filter(Boolean);
  const maxGifts = config.maxGifts || 1;
  const cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
  const blockedKey = 'gwp-blocked';
  const stateKey = 'gwp-state';

  let busy = false;
  let pending = null;
  let rechecks = 0;
  let justAdded = null;
  let autoAdding = false;
  let lastCart = null;

  const isGift = (item) => Boolean(item.properties && item.properties[property]);
  const unitPrice = (item) => item.final_line_price / Math.max(item.quantity, 1);

  // A gift product the customer added on its own, with a whole unit made free by the gift discount
  // (100% off), not just a large saving from another discount.
  const freeUnit = (item) => (item.line_level_discount_allocations || []).some((allocation) => {
    const application = allocation.discount_application || {};
    return application.value_type === 'percentage' && Number(application.value) >= 100 &&
      allocation.amount >= item.original_price;
  });
  const freeElsewhere = (cart) => cart.items.some((item) => !isGift(item) &&
    giftIds.includes(item.product_id) && item.original_price > 0 && freeUnit(item));

  const cartSignature = (cart) => (cart ? cart.items.map((item) => `${item.key}:${item.quantity}:${item.final_line_price}`).join('|') : '');

  // A gift the discount refused stays hidden until the paid items in the cart change.
  const paidSignature = (cart) => cart.items
    .filter((item) => !isGift(item))
    .map((item) => `${item.variant_id}:${item.quantity}`)
    .sort()
    .join('|');

  const readBlocked = () => {
    try {
      return JSON.parse(window.sessionStorage.getItem(blockedKey)) || { signature: null, products: [] };
    } catch (e) {
      return { signature: null, products: [] };
    }
  };

  const writeBlocked = () => {
    try {
      window.sessionStorage.setItem(blockedKey, JSON.stringify(blocked));
    } catch (e) {}
  };

  let blocked = readBlocked();

  // A shopper who removes the gift has opted out for that cart. Kept per cart token, so it holds
  // across page loads and tabs and starts afresh with a new cart; in memory too, for browsers that
  // refuse storage.
  let memoryState = {};
  const readState = () => {
    try {
      return JSON.parse(window.localStorage.getItem(stateKey)) || memoryState;
    } catch (e) {
      return memoryState;
    }
  };

  const setDeclined = (token, declined) => {
    memoryState = { token: token, declined: declined };
    try {
      window.localStorage.setItem(stateKey, JSON.stringify(memoryState));
    } catch (e) {}
  };

  const isDeclined = (cart) => {
    const state = readState();
    return Boolean(state.declined && state.token === cart.token);
  };

  const giftProperties = () => {
    const properties = {};
    properties[property] = config.propertyValue || 'Gift with purchase';
    return properties;
  };

  const applyBlocked = () => {
    document.querySelectorAll('[data-gwp-offer]').forEach((offer) => {
      const items = offer.querySelectorAll('[data-gwp-item]');
      let visible = 0;
      items.forEach((item) => {
        item.hidden = blocked.products.includes(Number(item.getAttribute('data-gwp-product')));
        if (!item.hidden) {
          visible++;
        }
      });
      offer.hidden = items.length > 0 && visible === 0;
    });
  };

  const post = (url, body) => window.fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify(body)
  }).then((response) => response.json().then((json) => {
    if (!response.ok) {
      throw json;
    }
    return json;
  }));

  const getCart = () => window.fetch(`${cartUrl}.js`).then((response) => response.json());

  const reload = () => document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));

  // The part of the cart the theme hides when it empties: the outermost [data-cart-form] around the items.
  const cartForm = (list) => {
    let form = list;
    for (let node = list.parentElement; node; node = node.parentElement) {
      if (node.hasAttribute('data-cart-form')) {
        form = node;
      }
    }
    return form;
  };

  // A render the theme started before a fix can land after it and show the old cart. For a few
  // seconds after publishing a fix, compare what the cart shows with the cart published and
  // render again if they differ (at most twice).
  let expected = null;
  const checkRender = () => {
    if (!expected || Date.now() > expected.until || expected.renders >= 2) {
      return;
    }
    const cart = expected.cart;
    const stale = Array.prototype.some.call(document.querySelectorAll('[data-line-items]'), (list) => {
      if (cartForm(list).classList.contains('cart--hidden')) {
        return false;
      }
      return !cart.item_count || list.querySelectorAll('[data-cart-item]').length !== cart.items.length;
    });
    if (stale) {
      expected.renders++;
      reload();
    }
  };

  // Tell the theme the cart changed: header count, free shipping bar, then the drawer re-renders.
  // The listener below skips this change event: it is ours, not the customer's.
  let publishing = false;
  const publish = (cart) => {
    publishing = true;
    try {
      document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true }));
      reload();
    } finally {
      publishing = false;
    }
    expected = { cart: cart, until: Date.now() + 5000, renders: 0 };
  };

  // The theme can render the drawer from the empty cart it read at page load when the first item
  // goes in; once the real cart arrives with items, render again (once per cart, so never a loop).
  let refreshedFor = null;
  const showItemsIfEmptyShown = (cart) => {
    const drawer = document.getElementById('drawer-cart');
    const empty = drawer && drawer.querySelector('[data-cart-empty]');
    if (!cart.item_count || !empty || empty.classList.contains('cart--hidden')) {
      refreshedFor = null;
      return;
    }
    const signature = `${cart.item_count}|${cart.total_price}|${paidSignature(cart)}`;
    if (refreshedFor !== signature) {
      refreshedFor = signature;
      reload();
    }
  };

  // A notice above the cart's contents, outside the part the theme hides when the cart empties.
  // Its own element: the theme's error box closes itself after a change.
  const notify = (message, reveal) => {
    document.querySelectorAll('[data-line-items]').forEach((list) => {
      const anchor = cartForm(list);
      let notice = anchor.parentNode.querySelector(':scope > [data-gwp-notice]');
      if (!notice) {
        notice = document.createElement('p');
        notice.className = 'gwp__notice';
        notice.setAttribute('data-gwp-notice', '');
        notice.setAttribute('role', 'status');
        anchor.parentNode.insertBefore(notice, anchor);
      }
      notice.textContent = message;
      notice.hidden = false;
      if (reveal && notice.offsetParent && notice.scrollIntoView) {
        notice.scrollIntoView({ block: 'nearest' });
      }
      window.clearTimeout(notice.gwpTimer);
      notice.gwpTimer = window.setTimeout(() => {
        notice.hidden = true;
      }, 8000);
    });
  };

  const clearNotice = () => {
    document.querySelectorAll('[data-gwp-notice]').forEach((notice) => {
      notice.hidden = true;
    });
  };

  // What a cart needs changing to keep gift lines right, or null when it is fine.
  const review = (cart) => {
    const signature = paidSignature(cart);
    if (blocked.signature !== signature) {
      blocked = { signature: signature, products: [] };
      writeBlocked();
    }

    const updates = {};
    const kept = [];
    const elsewhere = freeElsewhere(cart);
    let message = null;

    // Free lines first, so a gift split into a free and a charged line keeps the free one.
    const gifts = cart.items.filter(isGift).sort((a, b) => unitPrice(a) - unitPrice(b));

    gifts.forEach((item) => {
      const offered = config.enabled && giftIds.includes(item.product_id);
      if (!offered) {
        updates[item.key] = 0;
        message = config.enabled ? config.text.removed : (config.text.ended || config.text.removed);
        return;
      }

      // A second line of a gift already kept, or past the gift limit: take it out quietly.
      if (kept.includes(item.product_id) || kept.length >= maxGifts) {
        updates[item.key] = 0;
        return;
      }
      kept.push(item.product_id);

      // The discount gave its free unit to the same product added from its own page, so this gift
      // line would be charged. The shopper never chose to pay for it (the script added it, or they
      // asked for it free): take it out. The one they added is now their free gift.
      if (elsewhere && item.original_line_price - item.final_line_price < item.original_price) {
        updates[item.key] = 0;
        if (justAdded !== item.product_id) {
          message = config.text.moved || config.text.removed;
        }
        return;
      }

      if (item.quantity > 1) {
        updates[item.key] = 1;
        return;
      }

      if (item.final_line_price > 0) {
        updates[item.key] = 0;
        if (justAdded === item.product_id) {
          // The discount did not cover a gift just added: stop offering it for this cart.
          blocked.products.push(item.product_id);
          writeBlocked();
          message = config.text.unavailable;
        } else {
          message = config.text.removed;
        }
      }
    });

    return Object.keys(updates).length ? { updates: updates, message: message } : null;
  };

  // Apply fixes until the cart needs none (a quantity fix can reveal a gift that is not free).
  // Backstop: if something keeps undoing the fixes (another app re-adding a gift), stop after six
  // in ten seconds rather than keep sending requests.
  let fixTimes = [];
  const settle = (cart, depth) => {
    const fix = depth < 3 ? review(cart) : null;
    const now = Date.now();
    fixTimes = fixTimes.filter((time) => now - time < 10000);
    if (!fix || fixTimes.length >= 6) {
      return Promise.resolve({ cart: cart, message: null });
    }
    fixTimes.push(now);
    return post(`${cartUrl}/update.js`, { updates: fix.updates })
      .then((updated) => settle(updated, depth + 1))
      .then((result) => ({ cart: result.cart, message: result.message || fix.message }));
  };

  // Let go of the cart. If the cart changed while a fix was running, that change was skipped and
  // the cart just shown may be older than it: check it again, unless it matches what is shown
  // (an echo of our own update) or three re-checks have already run back to back.
  const release = (shown) => {
    busy = false;
    busyWas = false;
    const skipped = pending;
    pending = null;
    if (skipped && cartSignature(skipped) !== cartSignature(shown) && rechecks < 3) {
      rechecks++;
      busy = true;
      getCart().then((cart) => settle(cart, 0)).then(finish).catch(recover);
      return;
    }
    replayQuantity();
  };

  const finish = (result) => {
    const auto = autoAdding;
    justAdded = null;
    autoAdding = false;
    publish(result.cart);
    applyBlocked();
    // A gift put back by itself makes an earlier "removed" notice untrue.
    if (auto && result.cart.items.some(isGift)) {
      clearNotice();
    }
    // A gift the shopper never asked for is taken out quietly if the discount refuses it.
    if (auto && result.message === config.text.unavailable) {
      autoRefused = { token: result.cart.token, count: (autoRefused.token === result.cart.token ? autoRefused.count : 0) + 1 };
    }
    if (result.message && !(auto && result.message === config.text.unavailable)) {
      // A refused gift's card disappears, so bring the reason into view.
      notify(result.message, result.message === config.text.unavailable);
    }
    release(result.cart);
    settled(result.cart);
  };

  // If a fix could not be saved, read the cart again, try once more, and show the cart as it
  // really is rather than leave a stale drawer.
  const recover = () => getCart()
    .then((cart) => settle(cart, 2).catch(() => ({ cart: cart, message: null })))
    .then(finish)
    .catch(() => {
      justAdded = null;
      autoAdding = false;
      release(null);
    });

  document.addEventListener('theme:cart:change', function(event) {
    const cart = event.detail && event.detail.cart;
    if (publishing || !cart || !Array.isArray(cart.items)) {
      return;
    }
    // The lines shown must now match this newer cart before a held Check Out goes.
    if (expected) {
      expected.cart = cart;
    }
    if (busy) {
      pending = cart;
      return;
    }
    rechecks = 0;
    busy = true;
    Promise.resolve()
      .then(() => settle(cart, 0))
      .then((result) => {
        if (result.cart === cart) {
          applyBlocked();
          showItemsIfEmptyShown(cart);
          release(cart);
          settled(cart);
          return;
        }
        finish(result);
      })
      .catch(recover);
  });

  // Adds the gift a picker card offers, in the option its menu shows: the shopper's click, or by
  // itself once the cart qualifies (auto). An automatic add goes ahead only if the cart is still
  // the one the picker was drawn for.
  const addGift = (button, auto, keys) => {
    const item = button.closest('[data-gwp-item]');
    const select = item && item.querySelector('[data-gwp-variant]');
    const variantId = select ? Number(select.value) : 0;
    if (!variantId || busy) {
      return;
    }

    busy = true;
    autoAdding = auto;
    justAdded = Number(item.getAttribute('data-gwp-product'));
    button.setAttribute('disabled', true);
    button.classList.add('loading');
    if (!auto) {
      clearNotice();
    }

    // A picker drawn before the customer's own beanie was made free is out of date: the free one
    // is already in the cart, so show that instead of adding another (the discount would move
    // its free unit to the new line and charge for theirs).
    let added = false;
    getCart()
      .then((cart) => {
        if (freeElsewhere(cart) || (auto && cartKeys(cart) !== keys)) {
          return { cart: cart, message: null };
        }
        // The theme is changing the cart by line number: adding a line now would move its target.
        if (themeUpdating()) {
          throw { gwpWait: true };
        }
        if (auto) {
          countTry(cart);
        }
        return post(`${cartUrl}/add.js`, { items: [{ id: variantId, quantity: 1, properties: giftProperties() }] })
          .then(() => {
            added = true;
            // Adding it back by hand undoes an earlier opt-out.
            if (!auto) {
              setDeclined(cart.token, false);
            }
            return getCart();
          })
          .then((updated) => settle(updated, 0));
      })
      .then(finish)
      .catch((error) => {
        button.removeAttribute('disabled');
        button.classList.remove('loading');
        if (added) {
          // The gift went in but a later step failed: check and show the cart as it really is.
          recover();
          return;
        }
        justAdded = null;
        autoAdding = false;
        if (auto) {
          showOffers();
        } else if (!(error && error.gwpWait)) {
          notify((error && error.description) || config.text.error, true);
        }
        release(null);
      });
  };

  // The theme marks the cart as loading while it changes a quantity or removes a line.
  const themeUpdating = () => Boolean(document.querySelector('[data-cart-loading].cart--loading'));

  document.addEventListener('click', function(event) {
    const target = event.target;
    const button = target && target.closest ? target.closest('[data-gwp-add]') : null;
    if (!button) {
      return;
    }
    event.preventDefault();
    addGift(button, false, null);
  });

  // The cart's lines as the picker lists them (data-gwp-cart).
  const cartKeys = (cart) => cart.items.map((item) => `${item.key}:${item.quantity}`).sort().join('|');

  // Opt-out by default: once a settled cart's picker offers a gift, add it, unless the shopper
  // removed it from this cart or the offer is off. Runs after every settled cart and every
  // re-render, since either can be the last to arrive. Loop backstop: at most two automatic adds
  // in a row for the same paid items in a cart on a page, so a gift the discount keeps refusing (or
  // that something else keeps taking out) cannot loop; new paid items start the count again.
  let autoTries = { token: null, signature: null, count: 0 };
  // And once the discount has refused two automatic adds in a cart, stop for the page.
  let autoRefused = { token: null, count: 0 };
  const triesFor = (cart) => (autoTries.token === cart.token && autoTries.signature === paidSignature(cart) ? autoTries.count : 0);
  const countTry = (cart) => {
    autoTries = { token: cart.token, signature: paidSignature(cart), count: triesFor(cart) + 1 };
  };

  const autoAdd = () => {
    const cart = lastCart;
    if (busy || !cart || !cart.token || !config.enabled || !config.autoAdd || isDeclined(cart) ||
      triesFor(cart) >= 2 || (autoRefused.token === cart.token && autoRefused.count >= 2) || themeUpdating()) {
      return;
    }
    const keys = cartKeys(cart);
    const offer = Array.prototype.find.call(document.querySelectorAll('[data-gwp-offer]'),
      (el) => !el.hidden && el.getAttribute('data-gwp-cart') === keys);
    const button = offer && offer.querySelector('[data-gwp-item]:not([hidden]) [data-gwp-add]:not([disabled])');
    if (!button) {
      return;
    }
    // The card would only flash a spinner and vanish: hide it until the gift line arrives.
    offer.classList.add('gwp--adding');
    addGift(button, true, keys);
  };

  const showOffers = () => {
    document.querySelectorAll('.gwp--adding').forEach((offer) => {
      offer.classList.remove('gwp--adding');
    });
  };

  // A gift removed before the first cart arrived (a Remove link followed as a page load) is kept
  // as a pending opt-out for a couple of minutes and applied to the cart when it arrives.
  const pendingKey = 'gwp-decline';
  let declinePending = 0;
  const markPending = () => {
    declinePending = Date.now();
    try {
      window.localStorage.setItem(pendingKey, String(declinePending));
    } catch (e) {}
  };
  const takePending = () => {
    let since = declinePending;
    try {
      since = Math.max(since, Number(window.localStorage.getItem(pendingKey)) || 0);
      window.localStorage.removeItem(pendingKey);
    } catch (e) {}
    declinePending = 0;
    return since && Date.now() - since < 120000;
  };

  function settled(cart) {
    lastCart = cart;
    if (cart.token && takePending()) {
      setDeclined(cart.token, true);
    }
    if (autoTries.signature !== paidSignature(cart)) {
      autoTries = { token: cart.token, signature: paidSignature(cart), count: 0 };
    }
    replayQuantity();
    window.setTimeout(autoAdd, 0);
    releaseSubmit(false);
  }

  // Removing the free gift is the shopper opting out of it for this cart. Read from the shopper's
  // own action, not from the cart, so a late or out-of-order cart update is never taken for one.
  // The free gift is a _gwp line, or the gift product added from its own page that the discount
  // made free (taking that one out means no gift either).
  const freeGiftLine = (row, key) => {
    if (row && row.classList.contains('cart__items__row--gift')) {
      return true;
    }
    const item = lastCart && key ? lastCart.items.find((line) => line.key === key) : null;
    return Boolean(item && !isGift(item) && giftIds.includes(item.product_id) && freeUnit(item));
  };

  const decline = () => {
    if (lastCart && lastCart.token) {
      setDeclined(lastCart.token, true);
    } else {
      markPending();
    }
  };

  // The theme changes quantities and removes lines by line number, read just before it posts. While
  // this script is adding, swapping or fixing a line, the numbers are about to move, so a tap on
  // Remove or +/- waits (ignored for that moment) rather than land on the wrong line.
  // Only for the first 3 seconds of a change, so a slow request never leaves the cart unusable.
  let busySince = 0;
  let busyWas = false;
  const holding = () => {
    if (busy && !busyWas) {
      busySince = Date.now();
    }
    busyWas = busy;
    return busy && Date.now() - busySince < 3000;
  };

  const holdThemeAction = (event, element) => {
    if (!holding() || !element.closest('[data-line-items]')) {
      return false;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    return true;
  };

  // Quantities typed while held are applied once this script is done (a line whose key has
  // changed meanwhile is left as it is).
  let heldQuantity = null;
  const replayQuantity = () => {
    if (!heldQuantity || busy) {
      return;
    }
    const held = heldQuantity;
    heldQuantity = null;
    Object.keys(held).forEach((key) => {
      const input = Array.prototype.find.call(document.querySelectorAll('[data-update-cart]'),
        (field) => field.getAttribute('data-update-cart') === key);
      if (input) {
        try {
          input.value = held[key];
          input.dispatchEvent(new CustomEvent('change'));
        } catch (e) {}
      }
    });
  };

  document.addEventListener('click', function(event) {
    const target = event.target;
    const control = target && target.closest ? (target.closest('[data-remove-key]') ||
      target.closest('[data-increase-quantity]') || target.closest('[data-decrease-quantity]')) : null;
    if (!control || holdThemeAction(event, control)) {
      return;
    }
    if (control.hasAttribute('data-remove-key') &&
      freeGiftLine(control.closest('[data-cart-item]'), control.getAttribute('data-remove-key'))) {
      decline();
    }
  }, true);

  // A quantity typed in, or the stepper taken to 0 on a free beanie bought from its own page.
  document.addEventListener('change', function(event) {
    const target = event.target;
    const input = target && target.matches && target.matches('[data-update-cart]') ? target : null;
    if (!input) {
      return;
    }
    if (holdThemeAction(event, input)) {
      heldQuantity = heldQuantity || {};
      heldQuantity[input.getAttribute('data-update-cart')] = input.value;
      return;
    }
    if (Number(input.value) === 0 &&
      freeGiftLine(input.closest('[data-cart-item]'), input.getAttribute('data-update-cart'))) {
      decline();
    }
  }, true);

  // Check Out on the cart page posts the form's quantities by position. While the gift is being
  // added, swapped or fixed, or the lines shown are not yet the cart this script just published,
  // hold the submit until they are (3 seconds at most), so no quantity lands on the wrong line.
  const shownKeys = (form) => Array.prototype.map.call(form.querySelectorAll('[data-line-items] [data-remove-key]'),
    (link) => link.getAttribute('data-remove-key')).join('|');
  const formReady = (form) => !busy && !heldQuantity && !themeUpdating() && !(expected && Date.now() < expected.until &&
    shownKeys(form) !== expected.cart.items.map((item) => item.key).join('|'));
  let held = null;
  let releasing = false;
  function releaseSubmit(force) {
    if (!held || (!force && !formReady(held.form))) {
      return;
    }
    const form = held.form;
    const submitter = held.submitter;
    window.clearTimeout(held.timer);
    held = null;
    // Still not the cart's lines after 3 seconds: send no quantities at all (the cart keeps its
    // own), rather than quantities that may sit on the wrong lines.
    if (!formReady(form)) {
      form.querySelectorAll('[data-line-items] [name="updates[]"]').forEach((input) => {
        input.disabled = true;
      });
    }
    const button = submitter && submitter.isConnected ? submitter : null;
    releasing = true;
    try {
      if (form.requestSubmit) {
        form.requestSubmit(button || undefined);
      } else if (button) {
        button.click();
      } else {
        form.submit();
      }
    } finally {
      releasing = false;
    }
  }

  document.addEventListener('submit', function(event) {
    const form = event.target;
    if (releasing || !form || !form.querySelector || !form.querySelector('[data-line-items]')) {
      return;
    }
    if (held) {
      event.preventDefault();
      return;
    }
    if (formReady(form)) {
      return;
    }
    event.preventDefault();
    held = { form: form, submitter: event.submitter, timer: window.setTimeout(() => releaseSubmit(true), 3000) };
  }, true);

  // The gift line's menu: add the chosen option, then take the old line out, so the gift is never
  // lost on the way (the discount frees the newest gift line). A line's key changes when the
  // discount moves between lines, so the old line is found again by its variant after the add.
  // Our own update, so not an opt-out.
  let refocusSwap = false;
  const swapGift = (select) => {
    const variantId = Number(select.value);
    const original = Array.prototype.find.call(select.options, (option) => option.defaultSelected);
    const oldVariant = original ? Number(original.value) : 0;
    const reset = () => {
      if (original) {
        select.value = original.value;
      }
      select.disabled = false;
    };
    if (!variantId || !oldVariant || variantId === oldVariant || busy) {
      reset();
      return;
    }

    busy = true;
    refocusSwap = select.gwpKeyed === true;
    select.disabled = true;
    const row = select.closest('[data-cart-item]');
    const bar = row && row.querySelector('.item--loadbar');
    if (bar) {
      bar.style.display = 'block';
    }
    clearNotice();

    let added = false;
    getCart()
      .then((cart) => {
        const line = cart.items.find((item) => isGift(item) && item.variant_id === oldVariant);
        if (!line) {
          return settle(cart, 0);
        }
        // The theme is changing the cart by line number: adding a line now would move its target.
        if (themeUpdating()) {
          throw { gwpWait: true };
        }
        justAdded = line.product_id;
        return post(`${cartUrl}/add.js`, { items: [{ id: variantId, quantity: 1, properties: giftProperties() }] })
          .then(() => {
            added = true;
            return getCart();
          })
          .then((updated) => {
            const updates = {};
            updated.items.forEach((item) => {
              if (isGift(item) && item.variant_id === oldVariant) {
                updates[item.key] = 0;
              }
            });
            return Object.keys(updates).length ? post(`${cartUrl}/update.js`, { updates: updates }) : updated;
          })
          .then((updated) => settle(updated, 0));
      })
      .then(finish)
      .catch((error) => {
        if (bar) {
          bar.style.display = 'none';
        }
        if (added) {
          recover();
          return;
        }
        reset();
        justAdded = null;
        refocusSwap = false;
        if (!(error && error.gwpWait)) {
          notify((error && error.description) || config.text.error, true);
        }
        release(null);
      });
  };

  // Keyboard: arrow keys and typed letters step a closed menu one option at a time, firing a change
  // on each, so a keyboard choice is swapped once the shopper pauses, presses Enter or moves on.
  let keyedTimer = null;
  const commitKeyed = (select) => {
    window.clearTimeout(keyedTimer);
    keyedTimer = null;
    if (select.gwpPending) {
      select.gwpPending = false;
      swapGift(select);
    }
  };

  document.addEventListener('keydown', function(event) {
    const select = event.target && event.target.closest ? event.target.closest('[data-gwp-swap]') : null;
    if (!select) {
      return;
    }
    if (event.key === 'Enter') {
      commitKeyed(select);
    } else if (event.key !== 'Tab' && event.key !== 'Shift') {
      select.gwpKeyed = true;
    }
  }, true);

  document.addEventListener('pointerdown', function(event) {
    const select = event.target && event.target.closest ? event.target.closest('[data-gwp-swap]') : null;
    if (select) {
      select.gwpKeyed = false;
    }
  }, true);

  document.addEventListener('focusout', function(event) {
    const select = event.target && event.target.closest ? event.target.closest('[data-gwp-swap]') : null;
    if (select) {
      commitKeyed(select);
    }
  }, true);

  // The gift line's menu swaps the gift; the picker's menu moves the struck-through price.
  document.addEventListener('change', function(event) {
    const target = event.target;
    const swap = target && target.closest ? target.closest('[data-gwp-swap]') : null;
    if (swap) {
      if (swap.gwpKeyed) {
        swap.gwpPending = true;
        window.clearTimeout(keyedTimer);
        keyedTimer = window.setTimeout(() => commitKeyed(swap), 1200);
      } else {
        swapGift(swap);
      }
      return;
    }
    const select = target && target.closest ? target.closest('[data-gwp-variant]') : null;
    const item = select && select.closest('[data-gwp-item]');
    const price = item && item.querySelector('[data-gwp-price]');
    const option = select && select.options[select.selectedIndex];
    if (price && option && option.getAttribute('data-price')) {
      price.textContent = option.getAttribute('data-price');
    }
  });

  // The drawer swaps its line items on every change; re-hide refused gifts, check the render and
  // see whether the fresh picker offers a gift to add, each time.
  const observer = new MutationObserver(() => {
    applyBlocked();
    checkRender();
    if (autoAdding) {
      document.querySelectorAll('[data-gwp-offer]').forEach((offer) => {
        offer.classList.add('gwp--adding');
      });
    }
    releaseSubmit(false);
    if (refocusSwap && !busy) {
      refocusSwap = false;
      const active = document.activeElement;
      const select = document.querySelector('[data-gwp-swap]');
      if (select && (!active || active === document.body || !active.isConnected)) {
        select.focus();
      }
    }
    autoAdd();
  });
  document.querySelectorAll('[data-line-items]').forEach((list) => {
    observer.observe(list, { childList: true });
  });
  applyBlocked();

})();
